import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";
import { de, enGB } from "date-fns/locale";
import { LOAD_OVER, LOAD_WARN } from "@/components/ForecastChart";

const pad2 = (n) => String(n).padStart(2, "0");
// Local-date key (toISOString would shift the day in CET/CEST).
const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const round1 = (n) => Math.round(n * 10) / 10;

const BIG_CELLS = {
  head_cell: "text-muted-foreground rounded-md w-11 font-normal text-[0.8rem]",
  day: "inline-flex h-11 w-11 flex-col items-center justify-center rounded-md p-0 text-sm font-normal hover:bg-accent aria-selected:opacity-100",
};

export default function QuotaCalendar({ onDayClick, showOverbooking = false }) {
  const { t, lang } = useI18n();
  const [allUsage, setAllUsage] = useState({});
  const [approved, setApproved] = useState({});
  // Municipality view uses the same traffic light as the forecast: approved volumes only.
  const usage = showOverbooking ? approved : allUsage;
  const [quota, setQuota] = useState(0);
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(new Date());

  useEffect(() => {
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    api
      .get(`/quota/range?start=${fmt(start)}&end=${fmt(end)}`)
      .then((r) => {
        setQuota(r.data.quota_m3);
        setAllUsage(r.data.usage || {});
        setApproved(r.data.approved || {});
      })
      .catch(() => {});
  }, [month]);

  const excessOf = (key) => round1(Math.max((usage[key] || 0) - quota, 0));

  const dayInfo = (date) => {
    const used = usage[fmt(date)] || 0;
    return { used, pct: quota > 0 ? used / quota : 0 };
  };

  const modifiers = useMemo(() => {
    const over = [];
    const full = [];
    const near = [];
    const some = [];
    Object.keys(usage).forEach((k) => {
      const [y, m, d] = k.split("-").map(Number);
      const dd = new Date(y, m - 1, d);
      const pct = quota > 0 ? usage[k] / quota : 0;
      if (showOverbooking) {
        if (pct >= LOAD_OVER / 100) over.push(dd);
        else if (pct >= LOAD_WARN / 100) near.push(dd);
        else if (pct > 0) some.push(dd);
        return;
      }
      if (pct >= 1) full.push(dd);
      else if (pct >= 0.75) near.push(dd);
      else if (pct > 0) some.push(dd);
    });
    return { over, full, near, some };
  }, [usage, quota, showOverbooking]);

  const overbookedDays = useMemo(
    () => (showOverbooking ? Object.keys(usage).filter((k) => usage[k] - quota > 0.05).sort() : []),
    [usage, quota, showOverbooking],
  );

  const modifiersStyles = {
    over: { backgroundColor: "#EF233C", color: "white", borderRadius: 6 },
    full: { backgroundColor: "hsl(var(--destructive))", color: "white", borderRadius: 6 },
    near: showOverbooking
      ? { backgroundColor: "#FACC15", color: "#051923", borderRadius: 6 }
      : { backgroundColor: "#FFB703", color: "#051923", borderRadius: 6 },
    some: { backgroundColor: "hsl(var(--primary) / 0.2)", color: "hsl(var(--primary))", borderRadius: 6 },
    selected: { outline: "2px solid hsl(var(--foreground))", outlineOffset: 2, borderRadius: 6 },
  };

  const DayContent = ({ date }) => {
    const key = fmt(date);
    const excess = excessOf(key);
    const pct = quota > 0 ? (usage[key] || 0) / quota : 0;
    const level = pct >= LOAD_OVER / 100 ? "over" : pct >= LOAD_WARN / 100 ? "warn" : null;
    return (
      <span className="flex flex-col items-center leading-none" data-testid={level ? `cal-${level}-${key}` : undefined}>
        <span>{date.getDate()}</span>
        {excess > 0 && <span className="mt-0.5 font-mono-jb text-[9px] font-semibold">+{Math.round(excess)}</span>}
      </span>
    );
  };

  const selKey = fmt(selected);
  const selExcess = showOverbooking ? excessOf(selKey) : 0;

  return (
    <Card className="border-border">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("calendar")}</h3>
          <div className="label-caps">
            {quota} m³ / {t("date").toLowerCase()}
          </div>
        </div>
        <Calendar
          mode="single"
          locale={lang === "en" ? enGB : de}
          weekStartsOn={1}
          selected={selected}
          month={month}
          onMonthChange={setMonth}
          onSelect={(d) => {
            if (!d) return;
            setSelected(d);
            onDayClick && onDayClick(fmt(d), dayInfo(d));
          }}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          classNames={showOverbooking ? BIG_CELLS : undefined}
          components={showOverbooking ? { DayContent } : undefined}
          className="mx-auto w-fit rounded-md border border-border p-2 sm:p-3"
        />
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          {showOverbooking ? (
            <>
              <LegendDot color="hsl(var(--primary) / 0.5)" label={`< ${LOAD_WARN} %`} />
              <LegendDot color="#FACC15" label={t("forecast_load_warn")} />
              <LegendDot color="#EF233C" label={t("forecast_load_over")} />
            </>
          ) : (
            <>
              <LegendDot color="hsl(var(--primary) / 0.5)" label="< 75%" />
              <LegendDot color="#FFB703" label="≥ 75%" />
              <LegendDot color="hsl(var(--destructive))" label="100%" />
            </>
          )}
        </div>
        <div className="mt-3 rounded-md border border-border p-3" data-testid="cal-selected-info">
          <div className="label-caps">{formatDate(selKey)}</div>
          <div className="mt-1 font-mono-jb">
            {round1(usage[selKey] || 0)} / {quota} m³
            {showOverbooking && quota > 0 && <span className="ml-2 text-muted-foreground">· {t("forecast_load")} {Math.round(((usage[selKey] || 0) / quota) * 100)} %</span>}
          </div>
          {showOverbooking && (allUsage[selKey] || 0) - (approved[selKey] || 0) > 0.05 && (
            <div className="mt-1 text-xs text-muted-foreground" data-testid="cal-selected-pending">
              {t("forecast_pending")}: {round1((allUsage[selKey] || 0) - (approved[selKey] || 0))} m³
            </div>
          )}
          {selExcess > 0 && (
            <div className="mt-1 text-sm font-semibold text-destructive" data-testid="cal-selected-excess">
              {t("overbooking_over")}: {selExcess} m³
            </div>
          )}
        </div>
        {overbookedDays.length > 0 && (
          <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid="cal-overbooked-list">
            <div className="label-caps text-destructive">{t("overbooked_days")}</div>
            <ul className="mt-1 space-y-0.5 font-mono-jb text-sm">
              {overbookedDays.map((k) => (
                <li key={k} className="flex justify-between gap-3">
                  <span>{formatDate(k)}</span>
                  <span className="text-destructive">
                    {round1(usage[k])} / {quota} m³ · +{excessOf(k)} m³
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-block h-3 w-3 rounded-sm" style={{ background: color }} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
